import React, { useState } from "react";

import { Controller } from "react-hook-form";
import { Play, Pause } from "lucide-react";
import LanguageCards from "./LanguageCards";
import ApiService from "../../services/Api.service";
import toast from "react-hot-toast";

const LanguageVoiceForm = ({
  control,
  errors,
  languageOptions,
  voiceCharacters,
  voiceCharactersLoading,
  watchedLanguage,
  watchedVoiceGender,
  setValue,
  isCallBot = true,
}) => {
  const [playingId, setPlayingId] = useState(null);
  const [audioElement, setAudioElement] = useState(null);

  const handlePlayVoice = async (char) => {
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }

    if (playingId === char.id) {
      setPlayingId(null);
      setAudioElement(null);
      return;
    }

    try {
      setPlayingId(char.id);
      
      const res = await ApiService.playVoiceCharacter(char.id);
      
      if (res?.data?.signed_url || res?.data?.audio_url) {
        const audioUrl = res.data.signed_url || res.data.audio_url;
        const audio = new Audio(audioUrl);
        
        audio.onended = () => {
          setPlayingId(null);
          setAudioElement(null);
        };
        
        audio.onerror = () => {
          toast.error("Failed to play voice sample");
          setPlayingId(null);
          setAudioElement(null);
        };
        
        setAudioElement(audio);
        await audio.play();
      } else {
        toast.error("No audio sample available");
        setPlayingId(null);
      }
    } catch (error) {
      toast.error("Failed to play voice sample");
      setPlayingId(null);
    }
  };


  return (
    <div className="wizard-step-content">
      <h3 className="step-title">Language & Voice Character</h3>
      <p className="step-subtitle">Configure language and voice character settings</p>

      <div className="cw-form-field">
        <label className="cw-form-label">Select Language</label>
        <Controller
          name="language"
          control={control}
          render={({ field }) => (
            <LanguageCards
              languages={[
                ...(languageOptions?.default_language || []),
                ...(languageOptions?.other_languages || []),
              ]}
              selectedValue={field.value}
              onSelect={(lang) => field.onChange(lang.value)}
            />
          )}
        />
        {errors.language && (
          <span className="cw-error-text">{errors.language.message}</span>
        )}
      </div>
 
      {isCallBot && (
        <div className="cw-form-field" style={{ marginTop: 24 }}>
          <label className="cw-form-label">Voice Gender</label>
          <Controller
            name="voice_gender"
            control={control}
            render={({ field }) => (
              <div className="voice-gender-toggle">
                <button
                  type="button"
                  className={`voice-gender-btn ${field.value === "male" ? "active" : ""}`}
                  onClick={() => {
                    field.onChange("male");
                    setValue("voice_character_id", "");
                  }}
                >
                  Male
                </button>
                <button
                  type="button"
                  className={`voice-gender-btn ${field.value === "female" ? "active" : ""}`}
                  onClick={() => {
                    field.onChange("female");
                    setValue("voice_character_id", "");
                  }}
                >
                  Female
                </button>
              </div>
            )}
          />
        </div>
      )}
      {isCallBot && (
        <div className="cw-form-field" style={{ marginTop: 20 }}>
          <label className="cw-form-label">
            Select Voice Character
            {watchedLanguage && watchedVoiceGender && (
              <span className="filter-tag"> ({watchedLanguage} · {watchedVoiceGender})</span>
            )} 
          </label>
              {errors?.voice_character_id?.message && (
                <span className="cw-error-text">{errors.voice_character_id.message}</span>
              )}
          {!watchedLanguage || !watchedVoiceGender ? (
            <div className="voice-character-prompt">
              <div className="prompt-icon">🎙️</div>
              <div className="prompt-text">
                {!watchedLanguage ? "Select a language first" : "Select voice gender to view characters"}
              </div> 
            </div>
          ) : (
              <Controller
                name="voice_character_id"
                control={control}
                render={({ field }) => (
                  <div className="voice-character-grid">
                    {voiceCharactersLoading ? (
                      <div className="loading-text">Loading voice characters...</div>
                    ) : voiceCharacters.length === 0 ? (
                      <div className="empty-text">
                        No voice characters available for {watchedLanguage} · {watchedVoiceGender}
                      </div>
                    ) : (
                      voiceCharacters.map((char) => (
                        <div
                          key={char.id}
                          className={`voice-character-card ${field.value === char.id ? "selected" : ""}`}
                          onClick={() => field.onChange(char.id)}
                        >
                          <div className="voice-character-profile">
                            <div className="voice-character-avatar">
                              {char.gender === "male" ? "👨" : "👩"}
                            </div>
                            <div className="voice-character-info">
                              <div className="voice-character-name">{char.name}</div>
                              <div className="voice-character-lang">{char.language}</div>
                            </div>
                            <div className="voice-character-actions">
                              <button
                                type="button"
                                className={`play-voice-btn ${playingId === char.id ? "playing" : ""}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePlayVoice(char);
                                }}
                                title="Play voice sample"
                              >
                                {playingId === char.id ? <Pause size={14} /> : <Play size={14} />}
                              </button>
                            </div>
                          </div>
                          <div className="voice-character-desc">{char.description || "No description available"}</div>
                          <div className="voice-character-footer">
                            <div className="voice-character-tags">
                              <span className="tag language">{char.language}</span>
                              <span className="tag gender">{char.gender || "General"}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              />
          )}
        </div>
      )}
    </div>
  );
};

export default LanguageVoiceForm;
